import React, { useContext, useState } from "react";
import { StyleSheet, Text, View } from "react-native";

import {
  Input,
  NormalText,
  PrimaryButton,
  SecondaryButton,
  TitleText,
} from "../../../common/native";
import { SessionContext } from "../../../../contexts";
import { postClinicEmailVerificationSignal } from "../../../../services/api";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";
import {
  markClinicEmailVerificationSubmitted,
  normalizeEmail,
  validateClinicEmail,
} from "../../../../utils/clinicEmailVerification";
import { mixpanel } from "../../../../utils/mixpanel";

type ClinicEmailVerificationNudgeModalProps = {
  onClose: () => void;
};

const ClinicEmailVerificationNudgeModal: React.FC<
  ClinicEmailVerificationNudgeModalProps
> = ({ onClose }) => {
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;
  const [clinicEmail, setClinicEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isTouched, setIsTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [trialExtended, setTrialExtended] = useState(false);

  const validateCurrentEmail = () => {
    const nextError = validateClinicEmail(clinicEmail);
    setErrorMessage(nextError || "");
    return nextError;
  };

  const handleSubmit = async () => {
    setIsTouched(true);
    const validationError = validateCurrentEmail();
    if (validationError || !session) return;

    const normalizedClinicEmail = normalizeEmail(clinicEmail);
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await postClinicEmailVerificationSignal({
        clinic_email: normalizedClinicEmail,
        clinic_id: session.clinicId,
        clinic_name: session.clinicName,
      });

      markClinicEmailVerificationSubmitted(session.userId);
      setTrialExtended(response.trial_extended);
      if (response.free_trial_end_date) {
        sessionContext?.setSession((currentSession) =>
          currentSession
            ? {
                ...currentSession,
                freeTrialEndDate: response.free_trial_end_date,
              }
            : currentSession,
        );
      }
      mixpanel.track("Clinic Email Verification Submitted", {
        "User Email": session.userEmail,
        "Clinic Email": normalizedClinicEmail,
        "Clinic Id": session.clinicId,
        "Clinic Name": session.clinicName,
        "Trial Extended": response.trial_extended,
        source: "post_login_nudge",
      });
      setIsSubmitted(true);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "We could not save that email. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <View style={styles.container}>
        <View style={styles.badge}>
          <NormalText text="Clinic Bonus" style={styles.badgeText} />
        </View>
        <TitleText
          text="Clinic email saved"
          style={styles.header}
          fontWeight="600"
        />
        <NormalText
          text={
            trialExtended
              ? "Thanks. Enjoy your 7 extra days of simulations!"
              : "Thanks. Your clinic email has been saved."
          }
          style={styles.body}
        />
        <PrimaryButton text="Done" fullWidth onPress={onClose} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.badge}>
        <NormalText text="Clinic Bonus" style={styles.badgeText} />
      </View>
      <TitleText
        text="Verify your clinic email"
        style={styles.header}
        fontWeight="600"
      />
      <NormalText
        text="Unlock 7 extra days of simulations by adding your clinic email address."
        style={styles.body}
      />
      <View style={styles.form}>
        <Input
          type="email-address"
          name="Clinic Email Address"
          value={clinicEmail}
          placeholder="name@clinic.com"
          onChangeText={(value) => {
            setClinicEmail(value);
            if (isTouched) setErrorMessage("");
          }}
          onBlur={() => {
            setIsTouched(true);
            validateCurrentEmail();
          }}
          required
          accessibilityLabel="Clinic email address"
          autoCapitalize="none"
          autoComplete="email"
        />
        {!!errorMessage && (
          <Text
            style={styles.errorText}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
          >
            {errorMessage}
          </Text>
        )}
      </View>
      <PrimaryButton
        text="Save Clinic Email"
        fullWidth
        loading={isSubmitting}
        disabled={!clinicEmail.trim() || isSubmitting}
        onPress={handleSubmit}
      />
      <SecondaryButton
        text="Maybe Later"
        style={styles.secondaryButton}
        fullWidth
        disabled={isSubmitting}
        onPress={onClose}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_TOKENS.color.surface,
    padding: ADMIN_TOKENS.spacing.xxl,
    borderRadius: ADMIN_TOKENS.radius.xl,
    width: "100%",
    maxWidth: 520,
    alignItems: "center",
  },
  badge: {
    backgroundColor: ADMIN_TOKENS.color.surfaceAccent,
    borderColor: ADMIN_TOKENS.color.borderAccent,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderRadius: ADMIN_TOKENS.radius.xxl,
    paddingHorizontal: ADMIN_TOKENS.spacing.md,
    paddingVertical: ADMIN_TOKENS.spacing.sm,
    marginBottom: ADMIN_TOKENS.spacing.md,
  },
  badgeText: {
    color: ADMIN_TOKENS.color.accentDark,
    fontFamily: ADMIN_TOKENS.font.semibold,
    fontSize: ADMIN_TOKENS.fontSize.sm,
  },
  header: {
    textAlign: "center",
    marginBottom: ADMIN_TOKENS.spacing.md,
  },
  body: {
    textAlign: "center",
    marginBottom: ADMIN_TOKENS.spacing.xl,
    color: ADMIN_TOKENS.color.textSecondary,
    maxWidth: 440,
  },
  form: {
    width: "100%",
    marginBottom: ADMIN_TOKENS.spacing.lg,
  },
  errorText: {
    color: ADMIN_TOKENS.color.errorText,
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
    marginTop: ADMIN_TOKENS.spacing.sm,
  },
  secondaryButton: {
    marginTop: ADMIN_TOKENS.spacing.md,
  },
});

export default ClinicEmailVerificationNudgeModal;
